<template>
    <div class="client-upload-container">
      <!-- Custom Dropzone with Client Styling -->
      <div
        class="tw-border-2 tw-border-dashed tw-border-purple-300 tw-rounded-lg tw-p-4 tw-text-center tw-cursor-pointer"
        @drop.prevent="onDrop"
        @dragover.prevent
        @click="triggerFileInput"
      >
        <input
          ref="fileInput"
          type="file"
          class="tw-hidden"
          multiple
          @change="onFileSelected"
          :disabled="disabled"
        >

        <div class="tw-flex tw-flex-col tw-items-center tw-justify-center">
          <svg
            class="tw-h-8 tw-w-8 tw-text-purple-400"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <p class="tw-mt-2 tw-text-sm tw-text-purple-600">
            Drop files here or click to browse
          </p>
        </div>
      </div>

      <!-- File Progress Display -->
      <template v-if="displayFiles.length">
        <div
          v-if="displayVariant === 'full'"
          class="tw-mt-6 tw-space-y-4 tw-max-h-96 tw-overflow-y-auto"
        >
          <div
            v-for="file in displayFiles"
            :key="file.key"
            class="tw-flex tw-flex-col tw-bg-gray-100 tw-rounded-lg tw-p-4"
          >
            <!-- File header with name and status -->
            <div class="tw-flex tw-items-center">
              <svg
                class="tw-h-8 tw-w-8 tw-text-gray-400 tw-mr-3"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                  clip-rule="evenodd"
                />
              </svg>
              <div class="tw-flex-1">
                <p class="tw-text-sm tw-font-medium tw-text-gray-900">
                  {{ file.name }}
                </p>
                <!-- Progress bar -->
                <div class="tw-mt-1 tw-relative tw-pt-1">
                  <div class="tw-overflow-hidden tw-h-2 tw-text-xs tw-flex tw-rounded tw-bg-purple-200">
                    <div
                      :style="{ width: file.progress + '%' }"
                      class="tw-shadow-none tw-flex tw-flex-col tw-text-center tw-whitespace-nowrap tw-text-white tw-justify-center tw-bg-purple-500 tw-transition-all tw-duration-300"
                    />
                  </div>
                </div>
              </div>
              <span
                class="tw-ml-4 tw-text-sm"
                :class="getStatusClass(file.status)"
              >
                {{ file.status }}
              </span>
            </div>

            <!-- Document details section - only show when processing is complete -->
            <div
              v-if="file.status === 'Complete' && file.classification && file.classification !== 'N/A'"
              class="tw-mt-3 tw-pl-11"
            >
              <!-- Classification tag -->
              <div class="tw-flex tw-items-center tw-justify-between tw-mb-2" id="file-classification"">
                <span class="tw-bg-purple-100 tw-text-purple-800 tw-text-xs tw-font-medium tw-px-2.5 tw-py-0.5 tw-rounded-full">
                  {{ file.classification }}
                </span>
              </div>
              <!-- Summary -->
              <p v-if="file.summary" id="file-summary" class="tw-text-sm tw-text-gray-600">
                {{ file.summary }}
              </p>
            </div>

            <!-- Error details -->
            <div
              v-if="file.error"
              class="tw-mt-3 tw-pl-11 tw-text-red-600 tw-text-sm"
              id="file-error"
            >
              {{ file.error }}
            </div>
          </div>
        </div>

        <div
          v-else
          class="slim-lines"
        >
          <div
            v-for="file in displayFiles"
            :key="file.key"
            class="slim-line"
            :class="getStatusVariant(file.status)"
          >
          <p class="slim-line-name">{{ file.name }}</p>
          <div class="slim-classification">
            <Badge
              v-if="file.classification && file.status === 'Complete'"
              bold
              status="success"
            >
              {{ file.classification }}
            </Badge>
            <Loader v-else-if="file.status !== 'Complete'" />
          </div>
          <p class="slim-status">{{ file.status }}</p>
          <p v-if="file.error">
            {{ file.error }}
          </p>
          </div>
        </div>
      </template>

      <!-- Loading state -->
      <div
        v-if="isUploading && displayVariant === 'full'"
        class="tw-mt-4 tw-text-center tw-text-purple-600"
      >
        <div class="tw-inline-flex tw-items-center">
          <svg class="tw-animate-spin tw-h-5 tw-w-5 tw-mr-3" viewBox="0 0 24 24">
            <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
            <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Processing your documents...
        </div>
      </div>
    </div>
  </template>

  <script>
  import { useDocumentUploadStore } from '@frontend/ui/stores/DocmasterStore'
  import { Badge, Loader } from '@stakeui';

  export default {
    name: 'DocMasterUploadClient',
    props: {
      entityExternalId: {
        type: String,
        required: true,
      },
      advisorSlug: {
        type: String,
        required: false,
      },
      csrfToken: {
        type: String,
        required: true,
      },
      documentId: {
        type: [String, Number],
        required: false,
        default: 'doc',
      },
      displayVariant: {
        type: String,
        required: false,
        default: 'full',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      Badge,
      Loader,
    },
    emits: [
      'documents-updated',
      'document-classified',
      'checklist-item-crossed-off',
      'upload-progress',
      'upload-complete',
      'upload-error',
      'chat-message'
    ],
    data() {
      return {
        uploadStore: null,
        forceUpdate: 0,
        unsubscribeFunctions: [],
        isUploading: false,
      }
    },
    computed: {
      displayFiles() {
        // Force reactivity by referencing forceUpdate
        this.forceUpdate

        // Ensure we have the store and it's properly initialized
        if (!this.uploadStore || !this.uploadStore.currentEntityId) {
          return []
        }

        // Get session uploads and add progress/status from store
        const sessionUploads = this.uploadStore.currentSessionUploads || []
        const currentProgress = this.uploadStore.currentFileProgress || {}
        const currentStatuses = this.uploadStore.currentFileStatuses || {}

        return sessionUploads.map(doc => ({
          key: `${doc.external_id || Math.random()}-${doc.uploaded_at}`, // Ensure unique key
          name: doc.name || doc.filename,
          progress: currentProgress[doc.name] || (doc.status === 'completed' ? 100 : 0),
          status: this.getDisplayStatus(doc, currentStatuses[doc.name]),
          classification: doc.classification,
          summary: doc.info,
          error: doc.error,
          external_id: doc.external_id,
          processing: doc.processing
        }))
        }
    },
    async mounted() {
      // Initialize the store
      this.uploadStore = useDocumentUploadStore()

      // Configure store for client mode
      this.uploadStore.setMode('client')
      this.uploadStore.updateConfig({
        csrfToken: this.csrfToken,
        pollUntilFullyProcessed: false, // Client mode stops at classification
        enableDetailedErrors: false,    // Client-friendly errors
        autoChecklistCrossOff: true,    // Enable auto cross-off
      })

      // Set current entity
      try {
        await this.uploadStore.setCurrentEntityWithHistory(this.entityExternalId)
      } catch (error) {
        console.error('Failed to set current entity:', error)
      }

      // Set up event listeners
      this.setupEventListeners()

      // Initial reactivity update
      this.$nextTick(() => {
        this.forceUpdate++
      })
    },
    beforeUnmount() {
      // Clean up all event listeners
      this.unsubscribeFunctions.forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe()
        }
      })
      this.unsubscribeFunctions = []
    },
    methods: {
      setupEventListeners() {
        if (!this.uploadStore) return

        const eventHandlers = [
          { event: 'onDocumentClassified', handler: this.handleDocumentClassified },
          { event: 'onDocumentCompleted', handler: this.handleDocumentCompleted },
          { event: 'onChecklistCrossOff', handler: this.handleChecklistCrossOff },
          { event: 'onUploadProgress', handler: this.handleUploadProgress },
          { event: 'onUploadComplete', handler: this.handleUploadComplete },
          { event: 'onUploadStart', handler: this.handleUploadStart },
          { event: 'onError', handler: this.handleError },
          { event: 'onInvalidFilesIgnored', handler: this.handleInvalidFilesIgnored },
          { event: 'onSessionDocumentUpdated', handler: this.handleSessionDocumentUpdated },
        ]

        eventHandlers.forEach(({ event, handler }) => {
          const unsubscribe = this.uploadStore.on(event, handler)
          this.unsubscribeFunctions.push(unsubscribe)
        })
      },

      // Helper methods
      getDisplayStatus(doc, fileStatus) {
        if (doc.error) return 'Error'
        if (fileStatus) return fileStatus
        if (doc.status === 'completed' || (doc.classification && doc.classification !== 'N/A')) return 'Complete'
        if (doc.processing || doc.status === 'processing') return 'Processing...'
        return 'Uploaded'
      },

      getStatusClass(status) {
        return {
          'tw-text-purple-600': status === 'Processing...',
          'tw-text-green-600': status === 'Complete',
          'tw-text-red-600': status.includes('Error'),
          'tw-text-gray-500': status === 'Uploading' || status === 'Uploaded',
        }
      },

      getStatusVariant(status) {
        return {
          'status-processing': status === 'Processing...',
          'status-complete': status === 'Complete',
          'status-error': status.includes('Error'),
          'status-uploading': status === 'Uploading' || status === 'Uploaded',
        }
      },

      // File handling methods
      triggerFileInput() {
        if (this.disabled) {
          return;
        }
        this.$refs.fileInput?.click()
      },

      onFileSelected(event) {
        const files = Array.from(event.target.files)
        this.handleFiles(files)
        // Clear input so same file can be selected again
        event.target.value = ''
      },

      onDrop(event) {
        const files = Array.from(event.dataTransfer.files)
        this.handleFiles(files)
      },



      handleFiles(files) {
        // Validate files
        const isValidFileType = file => {
          // https://docs.reducto.ai/parsing/supported-file-formats
          const DOCMASTER_VALID_FILE_TYPES = [
            // PDF
            "application/pdf", "pdf", // PDF

            // Images / raster formats
            "image/png", "png",      // PNG
            "image/jpeg","jpeg",   // JPEG / JPG
            "image/gif", "gif",      // GIF
            "image/bmp", "bmp",      // BMP
            "image/tiff", "tiff",      // TIFF
            "image/heic","heic",       // HEIC
            // Note: Other formats Reducto supports (PCX, PPM, APNG, PSD, CUR, DCX, FTEX, PIXAR raster)
            // may not have widely used standard MIME types in browsers / uploads,
            // but you could include them if your upload layer provides MIME type for them.

            // Office / presentations / text docs
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
            "application/msword", // DOC
            "application/vnd.openxmlformats-officedocument.presentationml.presentation", // PPTX
            "application/vnd.ms-powerpoint", // PPT (older PowerPoint)

            // Spreadsheet formats
            "text/csv", "csv", // CSV
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
            "application/vnd.ms-excel", // XLS

            // Additional spreadsheet templates / macro-enabled types
            "application/vnd.openxmlformats-officedocument.spreadsheetml.template", // XLTX
            "application/vnd.ms-excel.sheet.macroEnabled.12", // XLSM
            "application/vnd.ms-excel.template.macroEnabled.12", // XLTM
            // (Quattro Pro QPW support may not have a standard MIME; include if available)

            // Text / simple formats
            "text/plain", // TXT
            "text/rtf", // RTF
            "text/html", // HTML (if you want HTML uploads supported as text-like)

            // Word templates
            "application/vnd.openxmlformats-officedocument.wordprocessingml.template", // DOTX
            
            // Legacy / other text-like
            // WordPerfect might not have a standard browser MIME (e.g., application/wordperfect),
          ];

          if (DOCMASTER_VALID_FILE_TYPES.includes(file.type)) {
            return true;
          }

          // fallback: extension-based check
          const ext = file.name.split(".").pop().toLowerCase();
          const validExts = [
            // PDF
            "pdf",
          
            // Images
            "jpg", "jpeg", "png", "gif", "bmp", "tif", "tiff", "heic",
          
            // Word
            "doc", "docx", "dotx",
          
            // Spreadsheets
            "csv", "xls", "xlsx", "xlsm", "xltx", "xltm",
          
            // Presentations
            "ppt", "pptx",
          
            // Text
            "txt", "rtf", "html"
          ];
          return validExts.includes(ext);
        };

        const validFiles = files.filter(isValidFileType);
        const invalidFiles = files.filter(file => !isValidFileType(file));

        // Alert about files that are not in the accepted file types
        if (invalidFiles.length > 0) {
          const fileNames = invalidFiles.map(f => f.name).join(', ')
          this.$emit('chat-message', `The following files are not accepted file types and won't be processed: ${fileNames}`)
        }

        // If no valid files, don't proceed
        if (validFiles.length === 0) {
          if (invalidFiles.length > 0) {
            this.$emit('chat-message', 'Please upload an accepted file type.')
          }
          return
        }

        // Upload files
        this.uploadFiles(validFiles)
      },

      async uploadFiles(files) {
        if (!this.uploadStore) {
          console.error('Upload store not initialized')
          return
        }

        try {
          this.$emit('chat-message', `Great! I've received your documents and I'm processing them now. I'll let you know once the analysis is complete.`)

          await this.uploadStore.uploadFiles(files, this.entityExternalId, this.advisorSlug, this.csrfToken)

          // Force reactivity update
          this.forceUpdate++

          this.$emit('upload-complete', { files, entityId: this.entityExternalId })

        } catch (error) {
          console.error('Upload failed:', error)
          this.$emit('upload-error', { error, files })
          this.$emit('chat-message', 'I encountered an error while uploading your documents. Please try again or contact support if the issue persists.')
        }
      },

      // Event handlers
      handleDocumentClassified(data) {
        console.log('Document classified:', data)

        // Force reactivity update
        this.forceUpdate++

        // Emit events for parent component
        this.$emit('document-classified', data)
        this.$emit('documents-updated')

        // Send chat message about classification
        const doc = data.document
        if (doc && doc.classification) {
          this.$emit('chat-message', `I've identified ${doc.name} as: ${doc.classification}`)
        }
      },

      handleDocumentCompleted(data) {
        console.log('Document completed:', data)

        // Force reactivity update
        this.forceUpdate++

        const doc = data.document
        if (doc) {
          // Send detailed completion message
          const message = `I've finished processing ${doc.name}. It's a ${doc.classification || 'document'}. ${doc.info || 'Document successfully classified'}`
          this.$emit('chat-message', message)

          this.$emit('documents-updated')
        }
      },

      handleChecklistCrossOff(data) {
        console.log('Checklist cross-off triggered:', data)
        this.$emit('checklist-item-crossed-off', data)

        // Force reactivity update
        this.forceUpdate++
      },

      handleUploadProgress(data) {
        // Force reactivity update when progress changes
        this.forceUpdate++
        this.$emit('upload-progress', data)
      },

      handleUploadStart(data) {
        console.log('Upload started:', data)
        this.isUploading = true
        this.forceUpdate++
      },

      handleUploadComplete(data) {
        console.log('Upload completed:', data)
        this.isUploading = false

        // Force reactivity update
        this.forceUpdate++

        this.$emit('upload-complete', data)
        this.$emit('documents-updated')
      },

      handleError(data) {
        console.error('Upload/processing error:', data)
        this.isUploading = false

        // Force reactivity update
        this.forceUpdate++

        this.$emit('upload-error', data)

        // Send user-friendly error message
        if (data.file) {
          this.$emit('chat-message', `There was an issue processing ${data.file.name}. ${data.error.message || 'Please try again.'}`)
        }
      },

      handleInvalidFilesIgnored(data) {
        const fileNames = data.files.map(f => f.name).join(', ')
        this.$emit('chat-message', `The following files are not accepted file types and won't be processed: ${fileNames}`)
      },

      handleSessionDocumentUpdated(data) {
        console.log('Session document updated:', data)

        // Force reactivity update
        this.forceUpdate++

        this.$emit('documents-updated')
      }
    }
  }
  </script>

<style scoped>
.client-upload-container {
  width: 100%;
}

/* Ensure smooth animations */
.tw-transition-all {
  transition: all 0.3s ease-in-out;
}

.tw-duration-300 {
  transition-duration: 300ms;
}

/* Loading spinner animation */
.tw-animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.slim-lines {
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  margin-top: 0.5em;
}

.slim-line {
  --bg: var(--stk-background);
  --fg: var(--stk-text-normal);
  background-color: var(--bg);
  color: var(--fg);
  display: grid;
  grid-template-columns: 1fr 10rem 6rem;
  font-size: 0.875em;
  padding: 0.25em 0.5em;
  border-radius: 0.5em;
  min-height: 2rem;
  align-items: center;
  gap: 1em;

  &.status-complete {
    --bg: var(--stk-success-90);
    --fg: var(--stk-success-30);
  }
}

.slim-line-name {
  word-break: break-word;
}

.slim-classification {
  display: flex;
  justify-content: end;
}
</style>
